import { useCallback, useEffect, useMemo, useState } from 'react';
import { DatePicker, Select, Space, Table, Tag, message } from 'antd';
import type { TableProps } from 'antd';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  formatDateTime,
  supabase,
  type ReminderEvent,
  type ReminderType,
} from '@ubus/shared';
import AdminListPage from '../components/AdminListPage';
import { useAdminTableStability } from '../hooks/useAdminTableStability';

type EventReader = {
  kind: 'admin_user' | 'driver';
  name: string;
  read_at: string;
};

type EventRow = ReminderEvent & {
  fleet_operator_name?: string | null;
  readers?: EventReader[];
};

function payloadFleetName(ev: ReminderEvent): string | null {
  const raw = ev.payload?.fleet_operator_name;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

async function attachFleetOperatorNames(events: ReminderEvent[]): Promise<EventRow[]> {
  if (events.length === 0) return [];

  const driverIds = [
    ...new Set(
      events
        .filter((e) => e.subject_kind === 'driver' && !payloadFleetName(e))
        .map((e) => e.subject_id)
    ),
  ];
  const vehicleIds = [
    ...new Set(
      events
        .filter((e) => e.subject_kind === 'vehicle' && !payloadFleetName(e))
        .map((e) => e.subject_id)
    ),
  ];

  const opIdBySubject = new Map<string, string>();
  const operatorIds = new Set<string>();

  for (const ev of events) {
    if (ev.subject_kind === 'fleet_operator') {
      opIdBySubject.set(`fleet_operator:${ev.subject_id}`, ev.subject_id);
      operatorIds.add(ev.subject_id);
    }
    const fromPayload = ev.payload?.fleet_operator_id;
    if (typeof fromPayload === 'string' && fromPayload) {
      opIdBySubject.set(`${ev.subject_kind}:${ev.subject_id}`, fromPayload);
      operatorIds.add(fromPayload);
    }
  }

  if (driverIds.length > 0) {
    const { data } = await supabase
      .from('drivers')
      .select('id, fleet_operator_id')
      .in('id', driverIds);
    for (const row of data || []) {
      if (row.fleet_operator_id) {
        opIdBySubject.set(`driver:${row.id}`, row.fleet_operator_id);
        operatorIds.add(row.fleet_operator_id);
      }
    }
  }
  if (vehicleIds.length > 0) {
    const { data } = await supabase
      .from('vehicles')
      .select('id, fleet_operator_id')
      .in('id', vehicleIds);
    for (const row of data || []) {
      if (row.fleet_operator_id) {
        opIdBySubject.set(`vehicle:${row.id}`, row.fleet_operator_id);
        operatorIds.add(row.fleet_operator_id);
      }
    }
  }

  const nameByOpId = new Map<string, string>();
  const ids = [...operatorIds];
  if (ids.length > 0) {
    const { data } = await supabase.from('fleet_operators').select('id, name').in('id', ids);
    for (const row of data || []) {
      nameByOpId.set(row.id, row.name || '');
    }
  }

  return events.map((ev) => {
    const fromPayload = payloadFleetName(ev);
    if (fromPayload) return { ...ev, fleet_operator_name: fromPayload };
    if (ev.subject_kind === 'fleet_operator') {
      return {
        ...ev,
        fleet_operator_name: ev.subject_label || nameByOpId.get(ev.subject_id) || null,
      };
    }
    const opId = opIdBySubject.get(`${ev.subject_kind}:${ev.subject_id}`);
    return {
      ...ev,
      fleet_operator_name: opId ? nameByOpId.get(opId) || null : null,
    };
  });
}

async function attachReadStatus(events: EventRow[]): Promise<EventRow[]> {
  if (events.length === 0) return [];
  const eventIds = events.map((e) => e.id);
  const { data, error } = await supabase
    .from('reminder_reads')
    .select('event_id, reader_kind, reader_id, reader_label, read_at')
    .in('event_id', eventIds)
    .order('read_at', { ascending: true });
  if (error) throw error;

  const needAdminIds = [
    ...new Set(
      (data || [])
        .filter((r) => r.reader_kind === 'admin_user' && !String(r.reader_label || '').trim())
        .map((r) => r.reader_id)
    ),
  ];
  const needDriverIds = [
    ...new Set(
      (data || [])
        .filter((r) => r.reader_kind === 'driver' && !String(r.reader_label || '').trim())
        .map((r) => r.reader_id)
    ),
  ];

  const adminNameById = new Map<string, string>();
  const driverNameById = new Map<string, string>();

  if (needAdminIds.length > 0) {
    const { data: profiles } = await supabase
      .from('portal_user_profiles')
      .select('auth_uid, display_name')
      .in('auth_uid', needAdminIds);
    for (const p of profiles || []) {
      if (p.display_name?.trim()) adminNameById.set(p.auth_uid, p.display_name.trim());
    }
  }
  if (needDriverIds.length > 0) {
    const { data: drivers } = await supabase
      .from('drivers')
      .select('id, name')
      .in('id', needDriverIds);
    for (const d of drivers || []) {
      if (d.name?.trim()) driverNameById.set(d.id, d.name.trim());
    }
  }

  const readersByEvent = new Map<string, EventReader[]>();
  for (const row of data || []) {
    const label =
      String(row.reader_label || '').trim() ||
      (row.reader_kind === 'admin_user'
        ? adminNameById.get(row.reader_id)
        : driverNameById.get(row.reader_id)) ||
      row.reader_id.slice(0, 8);
    const list = readersByEvent.get(row.event_id) || [];
    list.push({
      kind: row.reader_kind as 'admin_user' | 'driver',
      name: label,
      read_at: row.read_at,
    });
    readersByEvent.set(row.event_id, list);
  }

  return events.map((ev) => ({
    ...ev,
    readers: readersByEvent.get(ev.id) || [],
  }));
}

function isRead(row: EventRow): boolean {
  return (row.readers?.length || 0) > 0;
}

function ReminderRecords() {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState<EventRow[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [typeNames, setTypeNames] = useState<Map<string, { name_en: string; name_zh: string }>>(
    new Map()
  );
  const eventsTable = useAdminTableStability();
  const [recordTypeFilter, setRecordTypeFilter] = useState<string | undefined>();
  const [recordStatusFilter, setRecordStatusFilter] = useState<string | undefined>();
  const [recordReadFilter, setRecordReadFilter] = useState<'read' | 'unread' | undefined>();
  const [recordDueRange, setRecordDueRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const load = useCallback(async () => {
    setEventsLoading(true);
    try {
      const [{ data: eventData, error: eventErr }, { data: typeData, error: typeErr }] =
        await Promise.all([
          supabase
            .from('reminder_events')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(200),
          supabase.from('reminder_types').select('code, name_en, name_zh'),
        ]);
      if (eventErr) throw eventErr;
      if (typeErr) throw typeErr;
      const map = new Map<string, { name_en: string; name_zh: string }>();
      for (const row of (typeData || []) as Pick<ReminderType, 'code' | 'name_en' | 'name_zh'>[]) {
        map.set(row.code, { name_en: row.name_en, name_zh: row.name_zh });
      }
      setTypeNames(map);
      const withFleet = await attachFleetOperatorNames((eventData || []) as ReminderEvent[]);
      setEvents(await attachReadStatus(withFleet));
    } catch (err) {
      message.error(err instanceof Error ? err.message : t('reminders.recordsLoadFailed'));
    } finally {
      setEventsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const eventTypeLabel = (code: string) => {
    const meta = typeNames.get(code);
    if (!meta) return code;
    return i18n.language === 'zh-CN' ? meta.name_zh : meta.name_en;
  };

  const eventColumns: TableProps<EventRow>['columns'] = [
    {
      title: t('reminders.records.columns.created'),
      dataIndex: 'created_at',
      width: 170,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('reminders.records.columns.type'),
      dataIndex: 'type_code',
      width: 200,
      render: (code: string) => eventTypeLabel(code),
    },
    {
      title: t('reminders.records.columns.fleetOperator'),
      dataIndex: 'fleet_operator_name',
      width: 160,
      ellipsis: true,
      render: (v: string | null | undefined) => v || '—',
    },
    {
      title: t('reminders.records.columns.subject'),
      dataIndex: 'subject_label',
      ellipsis: true,
      render: (v: string | null, row) => v || `${row.subject_kind}:${row.subject_id.slice(0, 8)}`,
    },
    {
      title: t('reminders.records.columns.due'),
      dataIndex: 'due_date',
      width: 120,
    },
    {
      title: t('reminders.records.columns.ahead'),
      dataIndex: 'ahead_days',
      width: 90,
      render: (v: number | null) => (v == null ? '—' : String(v)),
    },
    {
      title: t('reminders.records.columns.status'),
      dataIndex: 'status',
      width: 100,
    },
    {
      title: t('reminders.records.columns.read'),
      key: 'read',
      width: 220,
      render: (_, row) => {
        const readers = row.readers || [];
        if (readers.length === 0) {
          return <Tag color="orange">{t('reminders.unread')}</Tag>;
        }
        return (
          <Space size={[4, 4]} wrap>
            {readers.map((r) => (
              <Tag
                key={`${r.kind}-${r.name}-${r.read_at}`}
                color={r.kind === 'driver' ? 'blue' : 'success'}
                title={formatDateTime(r.read_at)}
              >
                {r.kind === 'driver'
                  ? t('reminders.records.readByDriver', { name: r.name })
                  : t('reminders.records.readByAdmin', { name: r.name })}
              </Tag>
            ))}
          </Space>
        );
      },
    },
  ];

  const filteredEvents = useMemo(() => {
    return events.filter((ev) => {
      if (recordTypeFilter && ev.type_code !== recordTypeFilter) return false;
      if (recordStatusFilter && ev.status !== recordStatusFilter) return false;
      if (recordReadFilter === 'read' && !isRead(ev)) return false;
      if (recordReadFilter === 'unread' && isRead(ev)) return false;
      if (recordDueRange?.[0] && ev.due_date < recordDueRange[0].format('YYYY-MM-DD')) return false;
      if (recordDueRange?.[1] && ev.due_date > recordDueRange[1].format('YYYY-MM-DD')) return false;
      return true;
    });
  }, [events, recordTypeFilter, recordStatusFilter, recordReadFilter, recordDueRange]);

  const typeFilterOptions = useMemo(() => {
    const codes = [...new Set(events.map((e) => e.type_code))].sort();
    return codes.map((code) => ({ value: code, label: eventTypeLabel(code) }));
  }, [events, typeNames, i18n.language]);

  return (
    <AdminListPage
      title={t('nav.reminderRecords')}
      subtitle={t('reminders.records.subtitle')}
      widthMode="fluid"
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear
          placeholder={t('reminders.records.filterType')}
          style={{ minWidth: 220 }}
          options={typeFilterOptions}
          value={recordTypeFilter}
          onChange={(v) => setRecordTypeFilter(v)}
        />
        <Select
          allowClear
          placeholder={t('reminders.records.filterStatus')}
          style={{ minWidth: 140 }}
          options={[
            { value: 'open', label: 'open' },
            { value: 'resolved', label: 'resolved' },
            { value: 'cancelled', label: 'cancelled' },
          ]}
          value={recordStatusFilter}
          onChange={(v) => setRecordStatusFilter(v)}
        />
        <Select
          allowClear
          placeholder={t('reminders.records.filterRead')}
          style={{ minWidth: 140 }}
          options={[
            { value: 'unread', label: t('reminders.unread') },
            { value: 'read', label: t('reminders.read') },
          ]}
          value={recordReadFilter}
          onChange={(v) => setRecordReadFilter(v)}
        />
        <DatePicker.RangePicker
          allowEmpty={[true, true]}
          value={recordDueRange}
          onChange={(v) => setRecordDueRange(v)}
          placeholder={[t('reminders.records.filterDueFrom'), t('reminders.records.filterDueTo')]}
        />
      </Space>
      <Table
        rowKey="id"
        loading={eventsLoading}
        dataSource={filteredEvents}
        columns={eventColumns}
        pagination={eventsTable.pagination}
        onChange={eventsTable.onTableChange}
        scroll={{ x: 1160 }}
        locale={{ emptyText: t('reminders.records.empty') }}
      />
    </AdminListPage>
  );
}

export default ReminderRecords;
